/**
 * Transcript Editing Store
 *
 * Manages state for word-level transcript editing:
 * - Edit mode toggle (normal view vs edit mode)
 * - Popup editor state
 * - Word update operations with auto-save
 */

import { defineStore } from "pinia";
import { updateTranscriptWords } from "@/services/transcript.service";

export const useTranscriptEditingStore = defineStore("transcriptEditing", {
  state: () => ({
    // Edit Mode
    isEditMode: false,

    // Popup State
    showPopup: false,
    editingWordIndex: null,
    editingWordOriginalText: "",
    editingWordCurrentText: "",

    // Saving State
    isSaving: false,
    lastSaveError: null,

    // Track which words have been edited (for visual feedback)
    editedWordIndices: new Set(),

    // Character limit for words
    maxCharLimit: 30,
  }),

  getters: {
    /**
     * Check if current edit is a deletion (empty text)
     */
    isDeleting: (state) => {
      return state.editingWordCurrentText.trim() === "";
    },

    /**
     * Get current character count
     */
    charCount: (state) => {
      return state.editingWordCurrentText.length;
    },

    /**
     * Get instruction text based on edit state
     */
    instructionText: (state) => {
      if (state.editingWordCurrentText.trim() === "") {
        return "Press enter to remove word.";
      }
      return "Press enter to confirm the correction.";
    },

    /**
     * Check if text has changed from original
     */
    hasChanged: (state) => {
      return state.editingWordCurrentText !== state.editingWordOriginalText;
    },
  },

  actions: {
    /**
     * Enter edit mode
     */
    enterEditMode() {
      console.log("[TranscriptEditingStore] Entering edit mode");
      this.isEditMode = true;
      this.editedWordIndices = new Set();
    },

    /**
     * Exit edit mode
     */
    exitEditMode() {
      console.log("[TranscriptEditingStore] Exiting edit mode");
      this.isEditMode = false;
      this.closePopup();
    },

    /**
     * Open popup to edit a specific word
     *
     * @param {number} wordIndex - Index of the word in transcript
     * @param {string} currentText - Current text of the word
     */
    openPopup(wordIndex, currentText) {
      console.log("[TranscriptEditingStore] Opening popup for word:", {
        wordIndex,
        currentText,
      });

      this.editingWordIndex = wordIndex;
      this.editingWordOriginalText = currentText;
      this.editingWordCurrentText = currentText;
      this.showPopup = true;
      this.lastSaveError = null;
    },

    /**
     * Close popup without saving
     */
    closePopup() {
      console.log("[TranscriptEditingStore] Closing popup");
      this.showPopup = false;
      this.editingWordIndex = null;
      this.editingWordOriginalText = "";
      this.editingWordCurrentText = "";
    },

    /**
     * Update popup text (with character limit enforcement)
     *
     * @param {string} text - New text value
     */
    updatePopupText(text) {
      // Enforce max character limit
      this.editingWordCurrentText = text.slice(0, this.maxCharLimit);
    },

    /**
     * Confirm edit and save to backend
     *
     * @param {string} audioFileUUID - Audio file identifier
     * @param {string} workspaceUUID - Workspace identifier
     * @param {number} transcriptVersion - Current transcript version
     * @returns {Promise<Object>} Result with changed flag, new version, etc.
     */
    async confirmEdit(audioFileUUID, workspaceUUID, transcriptVersion) {
      const newText = this.editingWordCurrentText;
      const index = this.editingWordIndex;

      console.log("[TranscriptEditingStore] Confirming edit:", {
        index,
        originalText: this.editingWordOriginalText,
        newText,
      });

      // No change - just close
      if (newText === this.editingWordOriginalText) {
        console.log("[TranscriptEditingStore] No changes, closing popup");
        this.closePopup();
        return { changed: false };
      }

      this.isSaving = true;
      this.lastSaveError = null;

      try {
        // Auto-save immediately via API
        const result = await updateTranscriptWords(
          workspaceUUID,
          audioFileUUID,
          {
            transcriptVersion,
            updates: [{ index, text: newText }],
          },
        );

        // Track this word as edited
        this.editedWordIndices.add(index);

        const returnValue = {
          changed: true,
          index,
          newText,
          newVersion: result.transcriptVersion,
          summaryStale: result.summaryStale,
        };

        this.closePopup();

        return returnValue;
      } catch (error) {
        console.error("[TranscriptEditingStore] Save failed:", error);
        this.lastSaveError = error.message || "Failed to save";

        // Re-throw for component to handle
        throw error;
      } finally {
        this.isSaving = false;
      }
    },

    /**
     * Cancel edit operation
     */
    cancelEdit() {
      console.log("[TranscriptEditingStore] Edit cancelled");
      this.closePopup();
    },

    /**
     * Check if a word was recently edited (for visual feedback)
     *
     * @param {number} wordIndex - Word index to check
     * @returns {boolean} True if word was edited in current session
     */
    wasWordEdited(wordIndex) {
      return this.editedWordIndices.has(wordIndex);
    },

    /**
     * Reset all editing state
     */
    $reset() {
      this.isEditMode = false;
      this.showPopup = false;
      this.editingWordIndex = null;
      this.editingWordOriginalText = "";
      this.editingWordCurrentText = "";
      this.isSaving = false;
      this.lastSaveError = null;
      this.editedWordIndices = new Set();
    },
  },
});
